/**
 * API client for the Organization App (Tenant side)
 */

import { getCsrfHeaders, createFetchWithCsrfRetry } from '../utils/csrf';

export class OrgAppClientError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code: string, details?: unknown) {
    super(message);
    this.name = 'OrgAppClientError';
    this.code = code;
    this.details = details;
  }
}

type HandleResponseOptions = {
  skipAuthRedirect?: boolean;
};

async function handleResponse(response: Response, options: HandleResponseOptions = {}) {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      // Non-JSON error body (e.g. an HTML 504 page from a gateway): expose a
      // stable code callers can map to a friendly localized message.
      const code = response.status === 504 ? 'GATEWAY_TIMEOUT' : 'UNKNOWN_ERROR';
      throw new OrgAppClientError(`HTTP error ${response.status}`, code);
    }

    if (response.status === 401 && !options.skipAuthRedirect) {
      const redirectTarget = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${redirectTarget || '%2F'}`;
    }

    if (errorData?.error) {
      const msg = errorData.error.message || errorData.error.details?.message || errorData.error.details;
      const finalMsg = typeof msg === 'string' ? msg : (errorData.error.code || 'An error occurred');
      throw new OrgAppClientError(
        finalMsg,
        errorData.error.code || 'UNKNOWN_ERROR',
        errorData.error.details
      );
    }
    throw new OrgAppClientError(`HTTP error ${response.status}`, 'UNKNOWN_ERROR');
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

const BASE_URL = '/api/organizations-module';
const fetchWithCsrfRetry = createFetchWithCsrfRetry(`${BASE_URL}/auth/csrf`);

/** Company / fiscal identity of the current organization. Mirrors the backend DTO. */
export interface CompanyData {
  capital: string | null;
  patente: string | null;
  ice: string | null;
  if_number: string | null;
  rc: string | null;
  phone: string | null;
  fax: string | null;
  address: string | null;
  logo_url: string | null;
}

/** Editable single-line form fields (the logo has its own upload flow). */
export type CompanyDataField = Exclude<keyof CompanyData, 'logo_url'>;

/** One counted category of the org storage budget. Mirrors the backend DTO. */
export interface OrgStorageCategory {
  key: 'websites' | 'letterheads' | 'org_assets' | string;
  used_bytes: number;
}

/** Org-wide storage usage against the shared limit. Mirrors the backend DTO. */
export interface OrgStorageUsage {
  used_bytes: number;
  limit_bytes: number;
  categories: OrgStorageCategory[];
}

export const orgAppClient = {

  async getInvitationDetails(token: string) {
    const response = await fetch(`${BASE_URL}/auth/invitations/${token}`);
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async acceptInvitation(payload: Record<string, string>) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/auth/invitations/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify(payload)
    }));
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async getSession() {
    const response = await fetch(`${BASE_URL}/auth/session`);
    // A session *check* must never trigger the login redirect itself. When the
    // org JWT has expired but the cookie is still present, this endpoint
    // returns 401 — letting handleResponse redirect here would clobber the
    // `?redirect=` param on every `/login` reload (and can loop, since the
    // cookie isn't cleared on 401). Throw instead so refreshSession() clears
    // the session and AuthGuard performs the redirect with the correct
    // intended destination.
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async login(email: string, password: string) {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async requestMagicLink(email: string, redirect?: string) {
    const response = await fetch(`${BASE_URL}/auth/magic-links/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ email, redirect }),
    });
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async validateMagicLink(token: string) {
    const response = await fetch(`${BASE_URL}/auth/magic-links/${encodeURIComponent(token)}`);
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async claimMagicLink(token: string) {
    const response = await fetch(`${BASE_URL}/auth/magic-links/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ token }),
    });
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async refreshToken() {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    // Same rationale as getSession(): a token refresh is a background check
    // whose callers handle rejection by clearing the session. Don't hard-
    // redirect here — let AuthGuard do it cleanly with the intended page.
    return handleResponse(response, { skipAuthRedirect: true });
  },

  async switchOrganization(targetOrgId: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/auth/context/switch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ target_org_id: targetOrgId }),
    }));
    return handleResponse(response);
  },

  async getTutorialProgress(scopeType: string, scopeKey: string) {
    const response = await fetch(`${BASE_URL}/tutorials/${scopeType}/${scopeKey}`);
    return handleResponse(response);
  },

  async updateTutorialProgress(scopeType: string, scopeKey: string, status: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/tutorials/${scopeType}/${scopeKey}/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ status }),
    }));
    return handleResponse(response);
  },

  async getMembers() {
    const response = await fetch(`${BASE_URL}/members`);
    return handleResponse(response);
  },

  async createInvitation(data: { email: string; role_ids?: string[] }) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/invitations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify(data),
    }));
    return handleResponse(response);
  },


  async deleteMember(userId: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/members/${userId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        ...getCsrfHeaders(),
      },
    }));

    if (response.status === 204) return;
    return handleResponse(response);
  },

  async updateMemberRoles(userId: string, roleIds: string[]) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/members/${userId}/roles`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify({ role_ids: roleIds }),
    }));
    return handleResponse(response);
  },

  async deleteInvitation(invitationId: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async resendInvitation(invitationId: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/invitations/${invitationId}/resend`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async getRoles() {
    const response = await fetch(`${BASE_URL}/roles`);
    return handleResponse(response);
  },

  async getPermissions() {
    const response = await fetch(`${BASE_URL}/permissions`);
    return handleResponse(response);
  },

  async createRole(data: { name: string; description?: string; permissions: string[] }) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/roles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify(data),
    }));
    return handleResponse(response);
  },

  async updateRole(roleId: string, data: { name?: string; description?: string; permissions?: string[] }) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/roles/${roleId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify(data),
    }));
    return handleResponse(response);
  },

  async deleteRole(roleId: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/roles/${roleId}`, {
      method: 'DELETE',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async getCompanyData(): Promise<CompanyData> {
    const response = await fetch(`${BASE_URL}/company-data`);
    return handleResponse(response);
  },

  async getStorageUsage(): Promise<OrgStorageUsage> {
    const response = await fetch(`${BASE_URL}/storage`);
    return handleResponse(response);
  },

  /** Presign a direct browser upload for the org logo (org-assets bucket). */
  async getLogoUploadUrl(payload: { filename: string; content_type: string }): Promise<{
    upload_url: string;
    public_url: string;
  }> {
    const response = await fetchWithCsrfRetry(() =>
      fetch(`${BASE_URL}/company-data/logo/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCsrfHeaders(),
        },
        body: JSON.stringify(payload),
      }),
    )
    return handleResponse(response)
  },

  async updateCompanyData(payload: Partial<CompanyData>): Promise<CompanyData> {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/company-data`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
      },
      body: JSON.stringify(payload),
    }));
    return handleResponse(response);
  },

  async logout() {
    const response = await fetchWithCsrfRetry(() => fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  // ── Member search (people-picker) ───────────────────────────────────────────
  async searchMembers(q: string, limit = 10) {
    const response = await fetch(
      `${BASE_URL}/members/search?q=${encodeURIComponent(q)}&limit=${limit}`
    );
    return handleResponse(response);
  },
};

// ── Procurement saved filters ────────────────────────────────────────────────
const PROCUREMENT_BASE_URL = '/api/procurements-module';

/** Pagination sidecar of an extracted dossier file (`objects/text-pages/{sha}`).
 *  Line ranges are the canonical page boundaries (1-based, inclusive); char
 *  offsets are Python code-point offsets and must never slice JS strings. */
export interface ProcurementTextPages {
  version: number;
  page_count: number;
  source_page_count: number | null;
  truncated: boolean;
  pages: Array<{
    page: number;
    char_start: number;
    char_end: number;
    line_start: number;
    line_end: number;
  }>;
}

export const procurementClient = {
  async getSavedFilters() {
    const response = await fetch(`${PROCUREMENT_BASE_URL}/saved-filters/`);
    return handleResponse(response);
  },

  async getFiltersAudit() {
    const response = await fetch(`${PROCUREMENT_BASE_URL}/saved-filters/audit`);
    return handleResponse(response);
  },

  /** AI-assisted filter creation: generate review-only drafts (nothing saved). */
  async generateAiFilterDrafts(notes?: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${PROCUREMENT_BASE_URL}/saved-filters/ai-draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify(notes ? { notes } : {}),
    }));
    return handleResponse(response);
  },

  async createSavedFilter(payload: object) {
    const response = await fetchWithCsrfRetry(() => fetch(`${PROCUREMENT_BASE_URL}/saved-filters/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify(payload),
    }));
    return handleResponse(response);
  },

  async updateSavedFilter(id: string, payload: object) {
    const response = await fetchWithCsrfRetry(() => fetch(`${PROCUREMENT_BASE_URL}/saved-filters/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify(payload),
    }));
    return handleResponse(response);
  },

  async deleteSavedFilter(id: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${PROCUREMENT_BASE_URL}/saved-filters/${id}`, {
      method: 'DELETE',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async getAnalytics(params: {
    yearFrom?: number | null
    yearTo?: number | null
    buyer?: string | null
    mainCategory?: string | null
    procedureType?: string | null
    location?: string | null
    minAmount?: number | null
    maxAmount?: number | null
  }, signal?: AbortSignal) {
    const qs = new URLSearchParams()
    if (params.yearFrom != null) qs.set('year_from', String(params.yearFrom))
    if (params.yearTo != null) qs.set('year_to', String(params.yearTo))
    if (params.buyer) qs.set('buyer', params.buyer)
    if (params.mainCategory) qs.set('main_category', params.mainCategory)
    if (params.procedureType) qs.set('procedure_type', params.procedureType)
    if (params.location) qs.set('location', params.location)
    if (params.minAmount != null) qs.set('min_amount', String(params.minAmount))
    if (params.maxAmount != null) qs.set('max_amount', String(params.maxAmount))
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics?${qs}`, { signal })
    return handleResponse(response)
  },

  /** The participations table only (ENG-1): table paging/search/sort never
   * refetch the stats payload. Deadline is the only sort; the backend takes
   * its direction via sort_order. */
  async getAnalyticsParticipations(params: {
    yearFrom?: number | null
    yearTo?: number | null
    buyer?: string | null
    mainCategory?: string | null
    procedureType?: string | null
    location?: string | null
    minAmount?: number | null
    maxAmount?: number | null
    page?: number
    pageSize?: number
    search?: string | null
    status?: string | null
    sortOrder?: string | null
  }, signal?: AbortSignal) {
    const qs = new URLSearchParams({
      page: String(params.page ?? 1),
      page_size: String(params.pageSize ?? 20),
    })
    if (params.yearFrom != null) qs.set('year_from', String(params.yearFrom))
    if (params.yearTo != null) qs.set('year_to', String(params.yearTo))
    if (params.buyer) qs.set('buyer', params.buyer)
    if (params.mainCategory) qs.set('main_category', params.mainCategory)
    if (params.procedureType) qs.set('procedure_type', params.procedureType)
    if (params.location) qs.set('location', params.location)
    if (params.minAmount != null) qs.set('min_amount', String(params.minAmount))
    if (params.maxAmount != null) qs.set('max_amount', String(params.maxAmount))
    if (params.search) qs.set('search', params.search)
    if (params.status) qs.set('status', params.status)
    if (params.sortOrder) qs.set('sort_order', params.sortOrder)
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/participations?${qs}`, { signal })
    return handleResponse(response)
  },

  async getAnalyticsFunnel(params: { yearFrom?: number | null; yearTo?: number | null }, signal?: AbortSignal) {
    const qs = new URLSearchParams()
    if (params.yearFrom != null) qs.set('year_from', String(params.yearFrom))
    if (params.yearTo != null) qs.set('year_to', String(params.yearTo))
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/funnel?${qs}`, { signal })
    return handleResponse(response)
  },

  async getRadarCompetitors(signal?: AbortSignal) {
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/radar/competitors`, { signal })
    return handleResponse(response)
  },

  async getCompetitorRadar(
    competitor: string,
    period?: { yearFrom?: number | null; yearTo?: number | null } | null,
    signal?: AbortSignal,
  ) {
    const qs = new URLSearchParams({ competitor })
    if (period?.yearFrom != null) qs.set('year_from', String(period.yearFrom))
    if (period?.yearTo != null) qs.set('year_to', String(period.yearTo))
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/radar?${qs}`, { signal })
    return handleResponse(response)
  },

  async getAnalyticsMarket(params: { year?: number | null }, signal?: AbortSignal) {
    const qs = new URLSearchParams()
    if (params.year != null) qs.set('year', String(params.year))
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/market?${qs}`, { signal })
    return handleResponse(response)
  },

  async getMyMarket(
    params: { filterId?: string | null; yearFrom?: number | null; yearTo?: number | null },
    signal?: AbortSignal,
  ) {
    const qs = new URLSearchParams()
    if (params.filterId) qs.set('filter_id', params.filterId)
    if (params.yearFrom != null) qs.set('year_from', String(params.yearFrom))
    if (params.yearTo != null) qs.set('year_to', String(params.yearTo))
    const response = await fetch(`${PROCUREMENT_BASE_URL}/analytics/my-market?${qs}`, { signal })
    return handleResponse(response)
  },

  async getFiles(refConsultationId: string) {
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files`);
    return handleResponse(response);
  },

  async getFileSnapshots(refConsultationId: string, fileId: string) {
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/snapshots`);
    return handleResponse(response);
  },

  async getFileTree(refConsultationId: string, fileId: string, snapshotId?: string) {
    const qs = snapshotId ? `?snapshot=${snapshotId}` : '';
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}${qs}`);
    return handleResponse(response);
  },

  async getFileText(refConsultationId: string, fileId: string, path: string, snapshotId?: string): Promise<string> {
    const params = new URLSearchParams({ path });
    if (snapshotId) params.set('snapshot', snapshotId);
    // The `/text` endpoint does NOT return the markdown body — it returns a JSON
    // envelope `{ url }` holding a presigned S3 URL to the extracted text (or a 307
    // to S3 with `redirect=true`). Resolve the envelope, then GET the object
    // cross-origin. Bucket CORS allows GET; same two-step pattern as
    // fetchPreviewSignedUrl below and ai-chat/lib/procurementLoad.ts.
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/text?${params}`);
    const { url } = await handleResponse(response);
    const textResponse = await fetch(url);
    if (!textResponse.ok) {
      throw new OrgAppClientError(`HTTP error ${textResponse.status} fetching markdown`, 'UNKNOWN_ERROR');
    }
    return textResponse.text();
  },

  async getFileTextPages(refConsultationId: string, fileId: string, path: string, snapshotId?: string): Promise<ProcurementTextPages | null> {
    const params = new URLSearchParams({ path });
    if (snapshotId) params.set('snapshot', snapshotId);
    // Same envelope-then-object two-step as getFileText above, but for the
    // pagination sidecar (`objects/text-pages/{sha}` JSON). A 404 means the
    // file has no page map (pre-pagination extraction) — `null`, not an error:
    // callers fall back to the page-less markdown view.
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/text-pages?${params}`);
    if (response.status === 404) return null;
    const { url } = await handleResponse(response);
    const sidecarResponse = await fetch(url);
    if (!sidecarResponse.ok) {
      throw new OrgAppClientError(`HTTP error ${sidecarResponse.status} fetching page map`, 'UNKNOWN_ERROR');
    }
    return sidecarResponse.json();
  },

  getDownloadUrl(refConsultationId: string, fileId: string, path: string, snapshotId?: string) {
    const params = new URLSearchParams({ path });
    if (snapshotId) params.set('snapshot', snapshotId);
    return `${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/download?${params}`;
  },

  getArchiveUrl(refConsultationId: string, fileId: string, snapshotId?: string) {
    const params = new URLSearchParams({ redirect: 'true' });
    if (snapshotId) params.set('snapshot', snapshotId);
    return `${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/archive?${params}`;
  },

  getPreviewUrl(refConsultationId: string, fileId: string, path: string, snapshotId?: string, redirect = false) {
    const params = new URLSearchParams({ path });
    if (snapshotId) params.set('snapshot', snapshotId);
    if (redirect) params.set('redirect', 'true');
    return `${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/preview?${params}`;
  },

  async fetchPreviewSignedUrl(refConsultationId: string, fileId: string, path: string, snapshotId?: string): Promise<string> {
    const params = new URLSearchParams({ path });
    if (snapshotId) params.set('snapshot', snapshotId);
    const response = await fetch(`${PROCUREMENT_BASE_URL}/procurements/${refConsultationId}/files/${fileId}/preview?${params}`);
    const data = await handleResponse(response);
    return data.url;
  },
};

// ── Platform notifications ───────────────────────────────────────────────────
const NOTIFICATIONS_BASE_URL = '/api/notifications-module';

export const notificationsClient = {
  async getNotifications(page = 1, pageSize = 20, unreadOnly = false, sourceModule?: string) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      unread_only: String(unreadOnly),
    });
    if (sourceModule) params.set('source_module', sourceModule);
    const response = await fetch(`${NOTIFICATIONS_BASE_URL}/?${params}`);
    return handleResponse(response);
  },

  async getUnreadCount(sourceModule?: string) {
    const params = new URLSearchParams();
    if (sourceModule) params.set('source_module', sourceModule);
    const qs = params.toString();
    const response = await fetch(`${NOTIFICATIONS_BASE_URL}/unread-count${qs ? `?${qs}` : ''}`);
    return handleResponse(response);
  },

  async markRead(id: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${NOTIFICATIONS_BASE_URL}/${id}/read`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async markUnread(id: string) {
    const response = await fetchWithCsrfRetry(() => fetch(`${NOTIFICATIONS_BASE_URL}/${id}/unread`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async markAllRead(sourceModule?: string) {
    const params = new URLSearchParams();
    if (sourceModule) params.set('source_module', sourceModule);
    const qs = params.toString();
    const response = await fetchWithCsrfRetry(() => fetch(`${NOTIFICATIONS_BASE_URL}/read-all${qs ? `?${qs}` : ''}`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async archiveAll() {
    const response = await fetchWithCsrfRetry(() => fetch(`${NOTIFICATIONS_BASE_URL}/archive-all`, {
      method: 'POST',
      headers: getCsrfHeaders(),
    }));
    return handleResponse(response);
  },

  async getPreferences() {
    const response = await fetch(`${NOTIFICATIONS_BASE_URL}/preferences`);
    return handleResponse(response);
  },

  async updatePreferences(payload: object) {
    const response = await fetchWithCsrfRetry(() => fetch(`${NOTIFICATIONS_BASE_URL}/preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
      body: JSON.stringify(payload),
    }));
    return handleResponse(response);
  },
};
